import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import { AnimatePresence, motion, type Variants } from 'framer-motion'
import { Mic, Check, ListChecks, Loader, Loader2, MessageSquarePlus, Trash2, UserStar } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { FEATURE_FLAGS } from '@highlight/analytics'
import { useFlags } from '@highlight/analytics/react'
import { useGlobalEvent } from '@highlight/ipc/react'
import { Button, Logo } from '@highlight/ui'
import { FadingScrollable, MeetingIcon } from '@renderer/chat'
import {
  useActiveAudioNoteTitle,
  useAudioNoteAudioSessionId,
  useAudioNoteMeeting,
  useAudioNoteMeetingCalendarEventId,
  useAudioNoteSavedNote,
  useSavedAudioNoteId,
  useSummaryNoteId,
} from '@renderer/hooks/audio/use-audio-note-selectors'
import { useStagedActionJobs } from '@renderer/overlay/hooks/use-staged-action-jobs'
import { useSetSidepanelState } from '@renderer/overlay/atoms'
import { useSetSidepanelHotkeyOpened } from '@renderer/overlay/Sidepanel/atoms'
import { isPlannerV2Action, plannerVersion } from '@renderer/overlay/Sidepanel/hooks/staged-actions/confidence'
import { useArchiveStagedActions } from '@renderer/overlay/Sidepanel/hooks/staged-actions/mutations'
import { useActionRowModel } from '@renderer/overlay/Sidepanel/hooks/staged-actions/use-action-row-model'
import type { StagedAction } from '@renderer/overlay/Sidepanel/hooks/staged-actions/types'
import { getActionIcon, getActionTitle } from '@renderer/overlay/Sidepanel/utils/activity-helpers'
import { sendBody, sendReadiness } from '@renderer/overlay/Sidepanel/views/meeting-view/action-items/send-readiness'
import { trackActionEvent } from '@renderer/overlay/Sidepanel/views/action-view/track-action-event'
import type { MeetingActionItem } from '@renderer/overlay/Sidepanel/views/meeting-view/action-items/meeting-action-items'
import { useMeetingActionItems } from '@renderer/overlay/Sidepanel/views/meeting-view/action-items/use-meeting-action-items'
import type { ReminderItem } from '@renderer/overlay/Sidepanel/hooks/reminder-mapping'
import { useMeetingReminderActions } from '@renderer/overlay/Sidepanel/views/meeting-view/action-items/use-meeting-reminder-actions'
import { isMeetingPlannerRunning } from '@renderer/overlay/Sidepanel/views/meeting-view/meeting-follow-ups'
import { trackSidepanelOpened } from '@renderer/overlay/utils/track-sidepanel'
import { stagedActionEventProperties } from '@renderer/overlay/Sidepanel/hooks/staged-actions/track-staged-action'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { ConfidenceBar } from '@renderer/overlay/Sidepanel/components/confidence-bar'
import { getDetectedMeetingAppName } from '@shared/utils/audio-notes/AudioNoteUtils'
import { trackNotchAction } from '../../../track-notch'
import type { AudioNotePhase } from '../../../audio-note/use-audio-note-phase'
import { useOpenLiveMeetingInSidepanel, useOpenMeetingInSidepanel } from '../../../use-open-meeting-in-sidepanel'
import {
  notchCrossfadeTransition,
  notchCrossfadeVariants,
  notchEaseOut,
  notchEmergeFadeTransition,
} from '../notch/notch-motion'
import { useElementSize } from '../notch/use-element-size'
import { MINI_REVEAL_TRANSITION } from '../side-motion'
import { openInAppLink } from './action-links'

const NEW_ACTIONS_DURATION_MS = 6_000
/** The ended meeting shows Drafting at least this long, so it can't read as done before the final run starts, and at most this long. */
const DRAFTING_MIN_MS = 2_500
const DRAFTING_MAX_MS = 20_000

/** `new` lists what arrived since the list last closed; `all` lists the whole meeting. */
type CapturedListMode = 'new' | 'all'
type ListOpenTrigger = 'new action' | 'hover' | 'notch click'
type OpenMeetingTrigger = 'finished title' | 'drafting' | 'notch click' | 'captured count'
/** `emptied`: every listed row was dismissed or opened from somewhere else. */
type ListCloseTrigger = 'timeout' | 'phase' | 'emptied' | 'unhover'

/** Mid-meeting, new planner v2 actions and reminders pop the list open, and a hover or click lists them all. */
export function useCapturedActions(phase: AudioNotePhase, isHovered: boolean) {
  const liveSessionId = useAudioNoteAudioSessionId()
  // Main clears the session id on save, but the finished notch still lists that meeting's actions
  const [lastSessionId, setLastSessionId] = useState<string | null>(null)
  useEffect(() => {
    if (liveSessionId) setLastSessionId(liveSessionId)
  }, [liveSessionId])
  const sessionId = liveSessionId ?? (phase === 'saving' || phase === 'finished' ? lastSessionId : null)

  // The final run is often filed under the saved note; only once this meeting ends, as the last one's note id lingers
  const savedNoteId = useSavedAudioNoteId()
  const summaryNoteId = useSummaryNoteId()
  const endedNoteId = phase === 'saving' || phase === 'finished' ? (savedNoteId ?? summaryNoteId) : null
  const { items } = useMeetingActionItems(sessionId ?? undefined, endedNoteId ?? undefined)
  const [mode, setMode] = useState<CapturedListMode | null>(null)
  const [newIds, setNewIds] = useState<Array<string>>([])

  // Whether the list closes as the pointer leaves. A hover sets it by opening the list, and mid-meeting by
  // passing over an open one.
  const [leavesWithPointer, setLeavesWithPointer] = useState(false)
  // One drop-down per hover: closing the list under the pointer keeps it closed until the pointer leaves
  const [isHoverSpent, setIsHoverSpent] = useState(false)

  const openTriggerRef = useRef<ListOpenTrigger>('hover')
  const closeTriggerRef = useRef<ListCloseTrigger>('emptied')

  const closeBy = useCallback((trigger: ListCloseTrigger) => {
    closeTriggerRef.current = trigger
    setMode(null)
    setNewIds([])
    setLeavesWithPointer(false)
    setIsHoverSpent(true)
  }, [])

  const listNewItems = useCallback(
    (arrived: Array<string>) => {
      if (!mode) openTriggerRef.current = 'new action'
      setNewIds((ids) => [...ids, ...arrived.filter((id) => !ids.includes(id))])
      setMode((current) => current ?? 'new')
    },
    [mode],
  )

  useGlobalEvent('staged-action:new', (action: StagedAction) => {
    if (phase !== 'recording' || !sessionId || !isPlannerV2Action(action)) return
    if (!action.references?.some((reference) => reference.audioSessionId === sessionId)) return
    listNewItems([action.id])
  })
  useArrivingReminders(items, phase === 'recording', listNewItems)

  useEffect(() => {
    closeBy('phase')
  }, [phase, closeBy])

  // Hovering a recording meeting's notch drops its action items down
  useEffect(() => {
    if (!isHovered) {
      setIsHoverSpent(false)
      return
    }
    if (isHoverSpent || mode || items.length === 0 || phase !== 'recording') return
    openTriggerRef.current = 'hover'
    setMode('all')
    setLeavesWithPointer(true)
    setIsHoverSpent(true)
  }, [isHovered, isHoverSpent, mode, items.length, phase])

  const listed = useMemo(() => {
    if (!mode) return []
    return mode === 'new' ? items.filter((item) => newIds.includes(item.id)) : items
  }, [mode, items, newIds])
  // Drafting: the note saving or the meeting's final planner run
  const { jobs } = useStagedActionJobs()
  const [finishedAt, setFinishedAt] = useState<number | null>(null)
  const [, setDraftingTick] = useState(0)
  useEffect(() => {
    if (phase !== 'finished') {
      setFinishedAt(null)
      return
    }
    setFinishedAt(Date.now())
    const timers = [DRAFTING_MIN_MS, DRAFTING_MAX_MS].map((ms) =>
      window.setTimeout(() => setDraftingTick((tick) => tick + 1), ms),
    )
    return () => timers.forEach((timer) => window.clearTimeout(timer))
  }, [phase])
  const isPlanning = isMeetingPlannerRunning(jobs, {
    audioNoteId: savedNoteId ?? summaryNoteId,
    audioSessionId: sessionId,
  })
  const sinceFinished = finishedAt === null ? 0 : Date.now() - finishedAt
  const isDrafting =
    phase === 'saving' ||
    (phase === 'finished' && (sinceFinished < DRAFTING_MIN_MS || (isPlanning && sinceFinished < DRAFTING_MAX_MS)))

  // Unattended, the list folds away when a hover ends or after a heads-up
  useEffect(() => {
    if (!mode || phase !== 'recording') return
    if (isHovered) {
      setLeavesWithPointer(true)
      return
    }
    if (leavesWithPointer) {
      closeBy('unhover')
      return
    }
    const timeoutId = window.setTimeout(() => closeBy('timeout'), NEW_ACTIONS_DURATION_MS)
    return () => window.clearTimeout(timeoutId)
  }, [mode, isHovered, leavesWithPointer, phase, newIds.length, closeBy])

  const isDraftingShown = isDrafting
  const draftingStartedAtRef = useRef<number | null>(null)
  useEffect(() => {
    if (isDraftingShown) {
      draftingStartedAtRef.current ??= Date.now()
      return
    }
    const startedAt = draftingStartedAtRef.current
    draftingStartedAtRef.current = null
    if (startedAt === null || phase !== 'finished') return
    trackNotchAction('drafting finished', {
      durationMs: Date.now() - startedAt,
      timedOut: isPlanning,
      actionCount: items.length,
    })
  }, [isDraftingShown, phase, isPlanning, items.length])

  // A list already showing everything has nothing for a click to open
  const canOpen = items.length > 0 && mode !== 'all'
  const open = useCallback(() => {
    if (items.length === 0) return
    openTriggerRef.current = 'notch click'
    setMode('all')
    setLeavesWithPointer(false)
  }, [items.length])

  const isOpen = listed.length > 0

  // An ended meeting's actions open in the sidepanel, so only a recording notch lists them
  const isListShown = isOpen && phase === 'recording'
  // A hover over a list opened by new actions keeps it on just those, so the count stays new too
  const newCount = isListShown && mode === 'new' ? listed.length : null
  const openedAtRef = useRef<number | null>(null)
  useEffect(() => {
    if (isListShown === (openedAtRef.current !== null)) return
    if (isListShown) {
      openedAtRef.current = Date.now()
      closeTriggerRef.current = 'emptied'
      trackNotchAction('list opened', {
        trigger: openTriggerRef.current,
        phase,
        listedCount: listed.length,
        actionCount: items.length,
      })
      return
    }
    trackNotchAction('list closed', {
      trigger: closeTriggerRef.current,
      phase,
      durationMs: Date.now() - (openedAtRef.current ?? Date.now()),
    })
    openedAtRef.current = null
  }, [isListShown, phase, listed.length, items.length])

  return { items, listed, newCount, isOpen, isListShown, canOpen, open, isDrafting }
}

/**
 * Lists each reminder the meeting writes while it records. One already there
 * when the notch mounted isn't news, and the reminders query, unlike staged
 * actions, sends no event of its own for one that arrives.
 */
function useArrivingReminders(
  items: Array<MeetingActionItem>,
  isRecording: boolean,
  listNewItems: (ids: Array<string>) => void,
) {
  const [mountedAt] = useState(() => Date.now())
  const listedRef = useRef(new Set<string>())
  useEffect(() => {
    if (!isRecording) return
    const arrived = items.flatMap((item) =>
      item.kind === 'reminder' && item.createdAt >= mountedAt && !listedRef.current.has(item.id) ? [item.id] : [],
    )
    if (arrived.length === 0) return
    arrived.forEach((id) => listedRef.current.add(id))
    listNewItems(arrived)
  }, [items, isRecording, mountedAt, listNewItems])
}

interface EndedMeetingStatusProps {
  isDrafting: boolean
  hasActions: boolean
}

/** Drafting, then Drafted or Saved. While drafting a click on the notch opens the meeting, so it reads as a link. */
export function EndedMeetingStatus({ isDrafting, hasActions }: EndedMeetingStatusProps) {
  return (
    <AnimatePresence initial={false} mode="wait">
      <motion.span
        key={isDrafting ? 'drafting' : 'done'}
        className="flex items-center gap-2 whitespace-nowrap px-2 text-sm text-white"
        variants={notchCrossfadeVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        transition={notchCrossfadeTransition}
      >
        {isDrafting ? (
          <>
            <Loader aria-hidden="true" className="size-4 shrink-0 animate-spin text-white/80" />
            <span className="underline-offset-4 group-hover/notch:underline">Drafting</span>
          </>
        ) : (
          <>
            <Check aria-hidden="true" className="text-success size-4 shrink-0" />
            {hasActions ? 'Drafted' : 'Saved'}
          </>
        )}
      </motion.span>
    </AnimatePresence>
  )
}

const NAME_SCROLL_PX_PER_SECOND = 80
/** Fades whichever edge the name runs past: the end at rest, the start once scrolled. */
const NAME_FADE_END = 'linear-gradient(to right, black calc(100% - 40px), transparent)'
const NAME_FADE_START = 'linear-gradient(to left, black calc(100% - 40px), transparent)'

/** A name (after its icon) that sits still with its overflow faded and scrolls to its end while hovered; no `width` fills its row. */
function ScrollingName({ name, icon, width }: { name: string; icon?: ReactNode; width?: number }) {
  const [textRef, textSize] = useElementSize<HTMLSpanElement>()
  const [boxRef, boxSize] = useElementSize<HTMLSpanElement>()
  const [isHovered, setIsHovered] = useState(false)
  const room = width ?? boxSize.width
  // A name shorter than its width takes only what it needs, so a short title doesn't pad the notch
  const shownWidth = width === undefined ? undefined : textSize.width > 0 ? Math.min(width, textSize.width) : width
  const overflow = room > 0 ? Math.max(0, textSize.width - room) : 0
  const scrolls = isHovered && overflow > 0
  return (
    <span
      ref={width === undefined ? boxRef : undefined}
      className={twMerge('block overflow-hidden text-left', width === undefined && 'min-w-0 flex-1')}
      style={{ width: shownWidth, maskImage: overflow > 0 ? (scrolls ? NAME_FADE_START : NAME_FADE_END) : undefined }}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
    >
      <motion.span
        ref={textRef}
        className={twMerge('inline-block whitespace-nowrap', icon !== undefined && 'inline-flex items-center gap-2')}
        // Scrolls once to the end and holds there; only the pointer leaving brings it back
        animate={{ x: scrolls ? -overflow : 0 }}
        transition={
          scrolls
            ? { duration: overflow / NAME_SCROLL_PX_PER_SECOND, ease: 'linear', delay: 0.2 }
            : { duration: 0.3, ease: 'easeOut' }
        }
      >
        {icon}
        {name}
      </motion.span>
    </span>
  )
}

/** Opens the meeting that just ended in the sidepanel, once its note is saved. */
export function useOpenEndedMeeting() {
  const savedNote = useAudioNoteSavedNote()
  const activeTitle = useActiveAudioNoteTitle()
  const summaryNoteId = useSummaryNoteId()
  const openMeeting = useOpenMeetingInSidepanel()
  const name = savedNote?.title ?? activeTitle
  const noteId = savedNote?.id ?? summaryNoteId

  return useCallback(
    (trigger: OpenMeetingTrigger) => {
      if (!noteId) return
      trackNotchAction('open meeting', { trigger })
      openMeeting({ mode: 'note', audioNoteId: noteId, title: name ?? undefined })
    },
    [noteId, name, openMeeting],
  )
}

/** The meeting that just ended, by name (opening it), in the width the notch's cap leaves it. */
export function FinishedMeetingTitle({ width }: { width: number }) {
  const savedNote = useAudioNoteSavedNote()
  const activeTitle = useActiveAudioNoteTitle()
  const calendarEventId = useAudioNoteMeetingCalendarEventId()
  const liveMeeting = useAudioNoteMeeting()
  const openEndedMeeting = useOpenEndedMeeting()
  // Named like the drafting row, so the title shows before the saved note's broadcast lands
  const name = savedNote?.title ?? activeTitle
  // A saved note without a meeting was ad hoc; the live meeting may already be the next one detected
  const meetingAppName = getDetectedMeetingAppName(savedNote ? savedNote.meeting : liveMeeting)
  if (!name) return null

  return (
    <Button
      variant="ghost"
      size="sm"
      tone="on-dark"
      onClick={() => openEndedMeeting('finished title')}
      // Inset like an action row, so its icon and name line up with the rows' below
      className="shrink-0 justify-start"
    >
      {/* The icon scrolls with the name */}
      <ScrollingName
        name={name}
        width={width}
        icon={
          meetingAppName ? (
            <MeetingIcon appName={meetingAppName} size={16} />
          ) : (savedNote?.meeting?.calendarEventId ?? calendarEventId) ? (
            <Logo name="google-calendar" size={16} />
          ) : (
            <Mic aria-hidden="true" className="size-4 shrink-0 text-white/70" />
          )
        }
      />
    </Button>
  )
}

interface CapturedActionsChipProps {
  count: number
  /** Set while the notch lists only the actions that just arrived. */
  newCount: number | null
  isExpanded: boolean
}

/** The label grows at the chip's notch end, so it slides out from behind the notch with the row. */
const chipLabelVariants: Variants = {
  hidden: { opacity: 0, x: 4, transition: MINI_REVEAL_TRANSITION },
  visible: { opacity: 1, x: 0, transition: { ...MINI_REVEAL_TRANSITION, opacity: notchEmergeFadeTransition } },
}

const itemNoun = (count: number) => (count === 1 ? 'item' : 'items')

/**
 * A recording meeting's captured action count, spelled out while the notch is hovered. While new actions are
 * listed it counts only those, and crossfades back to the total once their list closes.
 */
export function CapturedActionsChip({ count, newCount, isExpanded }: CapturedActionsChipProps) {
  const openLiveMeeting = useOpenLiveMeetingInSidepanel()
  const label = newCount === null ? `${count} ${itemNoun(count)} for you` : `${newCount} new ${itemNoun(newCount)}`

  return (
    <Button
      variant="ghost"
      size="md"
      tone="on-dark"
      aria-label={label}
      className="px-1.5"
      onClick={(event) => {
        event.stopPropagation()
        trackNotchAction('open meeting', { trigger: 'captured count' })
        openLiveMeeting('recap')
      }}
    >
      <UserStar aria-hidden="true" className="size-4" />
      <AnimatePresence initial={false} mode="popLayout" anchorX="left">
        <motion.span
          key={newCount === null ? 'total' : 'new'}
          className="flex items-center gap-1 whitespace-nowrap"
          variants={notchCrossfadeVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          transition={notchCrossfadeTransition}
        >
          {newCount === null ? (
            <>
              {count}
              <AnimatePresence initial={false} mode="popLayout" anchorX="left">
                {isExpanded && (
                  <motion.span
                    key="label"
                    variants={chipLabelVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    {itemNoun(count)} for you
                  </motion.span>
                )}
              </AnimatePresence>
            </>
          ) : (
            `${newCount} new ${itemNoun(newCount)}`
          )}
        </motion.span>
      </AnimatePresence>
    </Button>
  )
}

interface CapturedActionListProps {
  items: Array<MeetingActionItem>
  label: string | null
  /** `Staged Action` source for its rows: the notch's own list, or the notch's toast. */
  source?: 'notch' | 'notch toast'
}

/** Four rows: `py-1.5` around `text-sm`'s 18px line. */
const LIST_MAX_HEIGHT_PX = 4 * 30

/** The label, then each row, fade in top to bottom as the notch opens around them; a row arriving later fades in alone. */
const listVariants: Variants = {
  hidden: {},
  visible: { transition: { delayChildren: 0.06, staggerChildren: 0.04 } },
}
const rowsVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.04 } },
}
const rowVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.18, ease: notchEaseOut } },
}

export function CapturedActionList({ items, label, source = 'notch' }: CapturedActionListProps) {
  const openAction = useOpenCapturedAction()

  return (
    <motion.div className="flex w-full flex-col" variants={listVariants} initial="hidden" animate="visible">
      {/* Inset like the rows, so the label and their icons share a left edge */}
      {label && (
        <motion.span variants={rowVariants} className="px-2 pb-[5px] pt-0.5 text-sm text-white/60">
          {label}
        </motion.span>
      )}
      <FadingScrollable minFadeSize="0px" style={{ maxHeight: LIST_MAX_HEIGHT_PX }}>
        <motion.ul className="flex flex-col" variants={rowsVariants}>
          {items.map((item) =>
            item.kind === 'reminder' ? (
              <CapturedReminderRow key={item.id} reminder={item.reminder} source={source} />
            ) : (
              <CapturedActionRow key={item.id} action={item.action} source={source} onOpen={openAction} />
            ),
          )}
        </motion.ul>
      </FadingScrollable>
    </motion.div>
  )
}

interface CapturedActionRowProps {
  action: StagedAction
  source: string
  onOpen: (openActionView: () => void) => void
}

const SENT_STATUSES = new Set(['pending', 'completed', 'undo-error'])

/** Takes no room until the row is hovered or focused, so the title runs to the row's end. */
const ON_HOVER = 'hidden group-focus-within:flex group-hover:flex'

function CapturedActionRow({ action, source, onOpen }: CapturedActionRowProps) {
  const { dismissStagedAction } = useArchiveStagedActions()
  const row = useActionRowModel(action, { eventSource: source })
  const title = getActionTitle(action)
  const readiness = sendReadiness(action)
  // A failed undo still left the message sent; an invoked chat or ticket isn't a send at all
  const isSent = readiness !== undefined && SENT_STATUSES.has(row.status)
  // Behind `notch-open-in-app` the action opens in its own app with the draft filled in
  const isOpenInAppEnabled = useFlags({ live: true }).isEnabled(FEATURE_FLAGS.NOTCH_OPEN_IN_APP, false)
  const latestAction = useRef(action)
  latestAction.current = action
  // Only once drafted: the gateway builds the link from the action's fields, so an earlier one opens an empty compose
  const isDrafted = readiness !== undefined && readiness.missing.length === 0 && readiness.drafted
  const isUndrafted = !isSent && readiness?.drafted === false
  const openInApp =
    isOpenInAppEnabled && isDrafted && !isSent && (row.isStaged || row.actionHasError)
      ? openInAppLink(action, () => sendBody(latestAction.current) || undefined)
      : null

  const handleOpenInApp = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      if (!openInApp) return
      openInApp.open()
      trackActionEvent(action, 'open-in-app', { source, status: row.status })
    },
    [action, openInApp, row.status, source],
  )

  const handleDismiss = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      dismissStagedAction(action.id).catch(console.error)
      AmplitudeRenderer.trackEvent('Staged Action', {
        action: 'archive',
        ...stagedActionEventProperties(action),
        status: row.status,
        source,
      })
    },
    [action, dismissStagedAction, row.status, source],
  )

  return (
    <motion.li
      variants={rowVariants}
      className="group ml-0 flex items-center rounded-lg hover:bg-white/10"
      data-staged-action-id={action.id}
      data-staged-action-tool-name={action.toolName}
      data-staged-action-tool-set={action.toolSet}
      data-staged-action-planner={plannerVersion(action)}
      data-staged-action-status={row.status}
    >
      <Button
        variant="ghost"
        size="lg"
        tone="on-dark"
        block
        data-notch-control={openInApp ? 'open action in app' : 'open action'}
        onClick={openInApp ? handleOpenInApp : () => onOpen(() => row.openActionView())}
        // The row is the hover surface, so the trailing buttons sit inside the fill; the title adds none of its own.
        className="flex-1 justify-start gap-2 px-2 hover:bg-transparent"
      >
        <span className="flex size-3.5 shrink-0 items-center justify-center">{getActionIcon(action, 14)}</span>
        {/* One line: a title too long for the notch fades, and scrolls to its end on hover */}
        <ScrollingName name={title} />
      </Button>
      <div className="mr-2 flex shrink-0 items-center gap-1">
        <ConfidenceBar action={action} className={twMerge('text-white/80', ON_HOVER)} />
        {isUndrafted && <span className="px-1 text-xs text-white/50">Not drafted</span>}
        {isSent ? (
          <span className="flex items-center gap-1 px-1 text-sm text-white/70">
            <Check aria-hidden="true" className="text-success size-4" />
            Sent
          </span>
        ) : (
          <>
            {/* A send already on its way can't be dismissed */}
            {row.isRunning ? (
              <Loader2 aria-label="Sending" className="m-1 size-4 animate-spin text-white/70" />
            ) : (
              <Button
                variant="ghost"
                size="sm"
                shape="square"
                tone="on-dark"
                aria-label={`Dismiss ${title}`}
                data-notch-control="delete action"
                onClick={handleDismiss}
                className={twMerge('shrink-0', ON_HOVER)}
              >
                <Trash2 aria-hidden="true" />
              </Button>
            )}
          </>
        )}
      </div>
    </motion.li>
  )
}

interface CapturedReminderRowProps {
  reminder: ReminderItem
  source: string
}

/** A reminder the meeting wrote: opens in the reminders list, or starts a chat about it. */
function CapturedReminderRow({ reminder, source }: CapturedReminderRowProps) {
  const { openReminder, startChat } = useMeetingReminderActions(reminder, source)
  const setHotkeyOpened = useSetSidepanelHotkeyOpened()

  const handleOpen = useCallback(() => {
    trackSidepanelOpened('mini')
    setHotkeyOpened(true)
    openReminder()
  }, [openReminder, setHotkeyOpened])

  const handleStartChat = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      trackSidepanelOpened('mini')
      startChat()
    },
    [startChat],
  )

  return (
    <motion.li
      variants={rowVariants}
      className="group ml-0 flex items-center rounded-lg hover:bg-white/10"
      data-reminder-id={reminder.id}
    >
      <Button
        variant="ghost"
        size="lg"
        tone="on-dark"
        block
        data-notch-control="open reminder"
        onClick={handleOpen}
        // The row is the hover surface, so the trailing buttons sit inside the fill; the title adds none of its own.
        className="flex-1 justify-start gap-2 px-2 hover:bg-transparent"
      >
        <ListChecks aria-hidden="true" className="size-3.5 shrink-0 text-white/80" />
        <ScrollingName name={reminder.title} />
      </Button>
      <div className="mr-2 flex shrink-0 items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          shape="square"
          tone="on-dark"
          aria-label={`Start a chat about ${reminder.title}`}
          data-notch-control="start reminder chat"
          onClick={handleStartChat}
          className={twMerge('shrink-0', ON_HOVER)}
        >
          <MessageSquarePlus aria-hidden="true" />
        </Button>
      </div>
    </motion.li>
  )
}

/** Opens the action's view in the sidepanel, where a chat action asks before it starts. */
function useOpenCapturedAction() {
  const setSidepanelState = useSetSidepanelState()
  const setHotkeyOpened = useSetSidepanelHotkeyOpened()

  return useCallback(
    (openActionView: () => void) => {
      trackSidepanelOpened('mini')
      setHotkeyOpened(true)
      setSidepanelState('open')
      openActionView()
    },
    [setHotkeyOpened, setSidepanelState],
  )
}
